import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { 
    Save, ArrowLeft, Download, Search, Plus, 
    Image as ImageIcon, Trash2, Sparkles, 
    Layers, CheckCircle2, X, FileText, MapPin, Eye, EyeOff
} from 'lucide-react';
import LocationMap from '../components/common/LocationMap';

import OptimizedImage from '../components/ui/OptimizedImage';
import { nativeDownload } from '../utils/nativeDownload';
import PdfOverlayEditor from '../components/apprentice/PdfOverlayEditor';

const FIELD_LIMITS = {
    botanical_name: 80,
    german_name: 80,
    family: 80,
    growth_form: 80,
    origin: 80,
    distribution: 80,
    endangerment: 50,
    observed_location: 80,
    height: 30,
    width: 30,
    leaf_info: 200,
    autumn_color: 80,
    life_zone: 80,
    flower_color: 80,
    flower_inflorescence: 200,
    fruit_info: 200,
    care_notes: 200,
    artname_erklärung: 200
};

const truncateText = (text, limit) => {
    if (!text || text.length <= limit) return text;
    return text.substring(0, limit - 1) + '…';
};

const InputField = ({ label, name, type = "text", colspan = 1, formData, handleChange, showSuggestion, handleSuggestion, suggestingField }) => (
    <div className={`col-span-1 md:col-span-${colspan}`}>
        <div className="flex justify-between items-end mb-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {label}
            </label>
            {showSuggestion && (
                <button
                    type="button"
                    onClick={() => handleSuggestion(name, label)}
                    disabled={suggestingField === name}
                    className="text-xs flex items-center gap-1 text-plantlix-500 hover:text-plantlix-700 dark:text-plantlix-400 font-medium transition-colors"
                    title="KI-Vorschlag generieren"
                >
                    {suggestingField === name ? <span className="animate-spin text-xs">⏳</span> : <span>🪄 Vorschlag</span>}
                </button>
            )}
        </div>
        {type === "textarea" ? (
            <textarea
                name={name}
                value={formData[name] || ''}
                onChange={handleChange}
                rows="2"
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 dark:text-white"
            />
        ) : (
            <input
                type={type}
                name={name}
                value={formData[name] || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 dark:text-white"
            />
        )}
    </div>
);

const Checkbox = ({ label, name, formData, handleChange }) => (
    <label className="flex items-center gap-2 cursor-pointer">
        <input
            type="checkbox"
            name={name}
            checked={formData[name] || false}
            onChange={handleChange}
            className="w-4 h-4 rounded border-gray-300 text-plantlix-500 focus:ring-plantlix-500"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
    </label>
);

const ArrayCheckbox = ({ label, arrayName, value, formData, handleArrayChange }) => {
    const checked = (formData[arrayName] || []).includes(value);
    return (
        <label className="flex items-center gap-2 cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={() => handleArrayChange(arrayName, value)}
                className="w-4 h-4 rounded border-gray-300 text-plantlix-500 focus:ring-plantlix-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
        </label>
    );
};

export default function PflanzeDerWocheEditor({ plants, categories, userPlantData }) {
    const { id } = useParams();
    const isNew = id === 'new';
    const navigate = useNavigate();
    const { fetchWithAuth, authToken } = useContext(AuthContext);
    const { showToast: addToast } = useToast();

    const [loading, setLoading] = useState(!isNew);
    const [pdfCoordinates, setPdfCoordinates] = useState(null);
    const [saving, setSaving] = useState(false);

    const [selectedPlantId, setSelectedPlantId] = useState('');
    const [selectedTrainingWeekId, setSelectedTrainingWeekId] = useState('');
    const [trainingWeeks, setTrainingWeeks] = useState([]);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [showMap, setShowMap] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSearchIds, setSelectedSearchIds] = useState([]);
    const [isCreatingBatch, setIsCreatingBatch] = useState(false);

    const [formData, setFormData] = useState({
        botanical_name: '', german_name: '', family: '', growth_form: '',
        artname_erklärung: '', origin: '', distribution: '',
        observed_location: '', height: '', width: '', leaf_info: '',
        autumn_color: '', life_zone: '', life_zones: [], flower_color: '',
        flower_inflorescence: '', fruit_info: '', sociality: '',
        watering_needs: '', lime_tolerance: '', soil_type: '',
        nutrient_needs: '', soil_ph: '', care_notes: '',
        is_protected: false, is_heimisch: false, is_intermediate_exam_subject: false,
        endangerment: '',
        ph_neutral: 'n', ph_sauer: 'n', ph_alkalisch: 'n',
        flowering_months: [],
        location_preferences: [],
        uses: [],
        kw_from: '', kw_to: ''
    });

    const [activeCategory, setActiveCategory] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // all | favorites | recent
    const [pdwImagePreview1, setPdwImagePreview1] = useState(null);
    const [pdwImagePreview2, setPdwImagePreview2] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    // DEBUG: Image State Tracking
    useEffect(() => {
        if (pdwImagePreview1 || pdwImagePreview2) {
            console.log("PDW Editor Image Previews:", { img1: !!pdwImagePreview1, img2: !!pdwImagePreview2 });
        }
    }, [pdwImagePreview1, pdwImagePreview2]);

    const [suggestingField, setSuggestingField] = useState(null);
    const [isBatchSuggesting, setIsBatchSuggesting] = useState(false);
    const [overwriteOnBatch, setOverwriteOnBatch] = useState(false);
    
    // Gallery State
    const [showGallery, setShowGallery] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);
    const [loadingGallery, setLoadingGallery] = useState(false);
    const [activeGalleryField, setActiveGalleryField] = useState(null); // 'image_1' or 'image_2'

    const [viewMode, setViewMode] = useState('form'); // 'form' or 'pdf'

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([
                fetchTrainingWeeks(),
                !isNew ? fetchEntry() : Promise.resolve(),
                fetchCoordinates()
            ]);
            setLoading(false);
        };
        init();
    }, [id]);

    const fetchTrainingWeeks = async () => {
        try {
            const response = await fetchWithAuth('/api/apprentice/weeks/');
            if (response.ok) {
                const data = await response.json();
                setTrainingWeeks(data);
            }
        } catch (error) {
            console.error('Error fetching training weeks:', error);
        }
    };

    const fetchEntry = async () => {
        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/`);
            if (response.ok) {
                const data = await response.json();
                setSelectedPlantId(data.plant?.toString() || '');
                setSelectedTrainingWeekId(data.training_week?.toString() || '');
                if (data.form_data) {
                    const fd = data.form_data;
                    // Backward-compat: derive life_zones array from life_zone string if not present
                    if (!fd.life_zones || fd.life_zones.length === 0) {
                        const VALID_CODES = ['G','GR','Fr','B','SH','H','St','FS','M','SF','MK','A','WR','W','KÜBEL'];
                        fd.life_zones = (fd.life_zone || '')
                            .split(',')
                            .map(s => s.trim())
                            .filter(s => VALID_CODES.includes(s));
                    }
                    setFormData(prev => ({ ...prev, ...fd }));
                }
                if (data.image_1) setPdwImagePreview1(data.image_1);
                if (data.image_2) setPdwImagePreview2(data.image_2);
                setLatitude(data.latitude);
                setLongitude(data.longitude);
                setShowMap(data.show_map ?? true);
            }
        } catch (error) {
            console.error('Error fetching entry:', error);
        }
    };

    const handleCreateBatchDrafts = async () => {
        if (selectedSearchIds.length === 0) return;
        setIsCreatingBatch(true);
        setLoading(true);
        try {
            const results = await Promise.all(selectedSearchIds.map(async (plantId) => {
                const plantRes = await fetchWithAuth(`/api/plants/${plantId}/`);
                let originalLifeZone = '';
                if (plantRes.ok) {
                    const plantData = await plantRes.json();
                    originalLifeZone = plantData.life_zone || '';
                }

                return fetchWithAuth('/api/apprentice/pflanze-der-woche/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        plant: plantId,
                        form_data: { ...formData, life_zone: originalLifeZone }
                    })
                });
            }));

            const allOk = results.every(res => res.ok);
            if (allOk) {
                addToast(`${selectedSearchIds.length} Entwürfe erfolgreich erstellt`, 'success');
                navigate('/apprentice/pflanze-der-woche');
            } else {
                addToast('Einige Entwürfe konnten nicht erstellt werden', 'warning');
                navigate('/apprentice/pflanze-der-woche');
            }
        } catch (error) {
            addToast('Fehler bei der Batch-Erstellung', 'error');
        } finally {
            setLoading(false);
            setIsCreatingBatch(false);
        }
    };


    const fetchCoordinates = async () => {
        try {
            const res = await fetchWithAuth('/api/pdf-coordinates/');
            if (res.ok) {
                const data = await res.json();
                setPdfCoordinates(data);
            } else {
                addToast('Fehler beim Laden der PDF-Koordinaten', 'error');
            }
        } catch (err) {
            console.error('Error fetching coordinates:', err);
        }
    };

    const handleCreateDraft = async (plantId) => {
        setLoading(true);
        try {
            const response = await fetchWithAuth('/api/apprentice/pflanze-der-woche/create_from_plant/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ plant_id: parseInt(plantId) })
            });

            if (response.ok) {
                const data = await response.json();
                addToast('Entwurf erstellt! Formulardaten wurden automatisch befüllt.', 'success');
                navigate(`/apprentice/pflanze-der-woche/${data.id}`);
            } else {
                addToast('Fehler beim Erstellen', 'error');
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            addToast('Fehler beim Erstellen', 'error');
            setLoading(false);
        }
    };

    const handleImageUpload = async (e, imageField) => {
        const file = e.target.files[0];
        if (!file) return;

        // Preview local
        const reader = new FileReader();
        reader.onloadend = () => {
            if (imageField === 'image_1') setPdwImagePreview1(reader.result);
            else if (imageField === 'image_2') setPdwImagePreview2(reader.result);
        };
        reader.readAsDataURL(file);

        // Upload to backend
        const uploadData = new FormData();
        uploadData.append(imageField, file);

        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/`, {
                method: 'PATCH',
                body: uploadData // fetchWithAuth needs to handle FormData by NOT setting Content-Type to JSON
            });
            if (response.ok) {
                // Reset transform metadata for the new image
                setFormData(prev => ({
                    ...prev,
                    [`${imageField}_transform`]: { rotation: 0, zoom: 1, crop: { x: 0, y: 0 }, is_deleted: false }
                }));
                addToast('Bild hochgeladen', 'success');
            } else {
                addToast('Fehler beim Bild-Upload', 'error');
            }
        } catch (error) {
            console.error(error);
            addToast('Fehler beim Bild-Upload', 'error');
        }
    };

    const handleGallerySelection = async (imageUrl) => {
        if (!activeGalleryField) return;
        
        // Update local preview
        if (activeGalleryField === 'image_1') setPdwImagePreview1(imageUrl);
        else if (activeGalleryField === 'image_2') setPdwImagePreview2(imageUrl);
        
        try {
            // Tell backend about the new image URL
            // Since we're picking from existing URLs, we use a special PATCH 
            // the backend needs to handle URLs if passed in image_1/image_2
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    [activeGalleryField + '_url']: imageUrl
                })
            });
            
            if (response.ok) {
                addToast('Bild ausgewählt', 'success');
                setShowGallery(false);
            } else {
                addToast('Fehler beim Übernehmen des Bildes', 'error');
            }
        } catch (error) {
            console.error(error);
            addToast('Fehler beim Übernehmen', 'error');
        }
    };

    const openGallery = async (field) => {
        if (isNew) {
            addToast("Bitte zuerst den Entwurf speichern.", "warning");
            return;
        }
        setActiveGalleryField(field);
        setShowGallery(true);
        setLoadingGallery(true);
        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/available_images/`);
            if (response.ok) {
                const data = await response.json();
                setGalleryImages(data.images || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingGallery(false);
        }
    };


    const handleSuggestion = async (fieldName, fieldLabel) => {
        if (!selectedPlantId && !formData.botanical_name) {
            addToast("Bitte zuerst einen Botanischen Namen eingeben oder aus der Liste wählen.", "warning");
            return;
        }
        setSuggestingField(fieldName);
        try {
            let currentId = id;
            if (isNew) {
                const payload = { plant: selectedPlantId ? parseInt(selectedPlantId) : null, form_data: formData };
                const response = await fetchWithAuth(`${API_URL}/api/apprentice/pflanze-der-woche/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (response.ok) {
                    const data = await response.json();
                    currentId = data.id;
                    navigate(`/apprentice/pflanze-der-woche/${currentId}`, { replace: true });
                } else {
                    throw new Error("Entwurf konnte nicht gespeichert werden.");
                }
            }

            const limit = FIELD_LIMITS[fieldName] || 200;
            const res = await fetchWithAuth(`${API_URL}/api/apprentice/pflanze-der-woche/${currentId}/research_suggestion/?field=${encodeURIComponent(fieldLabel)}&max_chars=${limit}`);

            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    const truncated = truncateText(data.suggestion, FIELD_LIMITS[fieldName] || 200);
                    setFormData(prev => ({ ...prev, [fieldName]: truncated }));
                    addToast(`Vorschlag für "${fieldLabel}" eingefügt.`, "success");
                    await fetchWithAuth(`${API_URL}/api/apprentice/pflanze-der-woche/${currentId}/`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ form_data: { ...formData, [fieldName]: truncated } })
                    });
                }
            } else {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.error || "Fehler bei der KI-Generierung.");
            }
        } catch (e) {
            addToast(e.message, "error");
        } finally {
            setSuggestingField(null);
        }
    };

    const handleMagicFillAndSave = async () => {
        if (isNew) {
            addToast("Bitte zuerst den Entwurf speichern.", "warning");
            return;
        }
        setIsBatchSuggesting(true);
        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/research_all_suggestions/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    overwrite: true,
                    field_limits: FIELD_LIMITS
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.form_data) {
                    // Update frontend state with returned data
                    setFormData(prev => ({ ...prev, ...data.form_data }));
                    addToast("Magic Fill erfolgreich! Automatisch gespeichert.", "success");
                    // Data is already saved by the backend in research_all_suggestions
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                addToast(errorData.error || "Fehler beim Magic Fill", "error");
            }
        } catch (error) {
            console.error(error);
            addToast("Magic Fill fehlgeschlagen", "error");
        } finally {
            setIsBatchSuggesting(false);
        }
    };

    const handleBatchSuggestion = async () => {
        if (isNew) {
            addToast("Bitte zuerst den Entwurf speichern.", "warning");
            return;
        }
        
        setIsBatchSuggesting(true);
        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/research_all_suggestions/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    overwrite: overwriteOnBatch,
                    field_limits: FIELD_LIMITS
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.form_data) {
                    setFormData(prev => ({ ...prev, ...data.form_data }));
                    addToast("Batch-Recherche abgeschlossen! Alle Felder wurden gefüllt.", "success");
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                addToast(errorData.error || "Fehler bei der Batch-Recherche", "error");
            }
        } catch (error) {
            console.error(error);
            addToast("Batch-Recherche fehlgeschlagen", "error");
        } finally {
            setIsBatchSuggesting(false);
        }
    };

    const handleSave = async () => {
        if (isNew) return; // Must select plant first
        setSaving(true);
        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    form_data: formData,
                    training_week: selectedTrainingWeekId || null,
                    latitude,
                    longitude,
                    show_map: showMap
                })
            });

            if (response.ok) {
                addToast('Erfolgreich gespeichert', 'success');
            } else {
                addToast('Fehler beim Speichern', 'error');
            }
        } catch (error) {
            console.error(error);
            addToast('Netzwerkfehler', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Möchtest du diesen Eintrag wirklich löschen?')) return;
        setSaving(true);
        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/`, {
                method: 'DELETE'
            });
            if (response.ok || response.status === 204) {
                addToast('Eintrag gelöscht', 'success');
                navigate('/apprentice/pflanze-der-woche', { replace: true });
            } else {
                addToast('Fehler beim Löschen', 'error');
                setSaving(false);
            }
        } catch (error) {
            console.error(error);
            addToast('Fehler beim Löschen', 'error');
            setSaving(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (isNew) return;
        await handleSave();
        
        const showGrid = localStorage.getItem('pdfGridInExport') === 'true';
        const url = `/api/apprentice/pflanze-der-woche/${id}/download_pdf/` + (showGrid ? '?grid=1' : '');
        
        await nativeDownload(
            url,
            authToken,
            (msg) => addToast(msg, 'error')
        );
    };

    const handleDownloadHerbariumPdf = async () => {
        if (isNew) return;
        
        const url = `/api/apprentice/pflanze-der-woche/${id}/download_herbarium_pdf/`;
        
        await nativeDownload(
            url,
            authToken,
            (msg) => addToast(msg, 'error')
        );
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleArrayChange = (arrayName, value) => {
        setFormData(prev => {
            const currentArray = prev[arrayName] || [];
            if (currentArray.includes(value)) {
                return { ...prev, [arrayName]: currentArray.filter(i => i !== value) };
            } else {
                return { ...prev, [arrayName]: [...currentArray, value] };
            }
        });
    };

    // Filter plants based on search query
    const filteredPlants = plants?.filter(p =>
        p.botanical_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.german_name && p.german_name.toLowerCase().includes(searchQuery.toLowerCase()))
    ).slice(0, 50) || [];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-8 flex justify-center items-center">
                <div className="absolute animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-plantlix-500"></div>
            </div>
        );
    }

    if (isNew) {
        // Derive filtered list based on all criteria
        const displayPlants = (plants || []).filter(plant => {
            const matchesSearch = searchQuery === '' || 
                plant.botanical_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (plant.german_name && plant.german_name.toLowerCase().includes(searchQuery.toLowerCase()));
            
            const matchesCategory = !activeCategory || plant.category === activeCategory;
            
            const userData = userPlantData?.[plant.id] || {};
            const matchesTab = activeTab === 'all' || 
                (activeTab === 'favorites' && userData.is_favorite) ||
                (activeTab === 'recent' && userData.learning_status === 'LEARNED');
                
            return matchesSearch && matchesCategory && matchesTab;
        }).slice(0, 48);

        return (
            <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-8 px-4">
                <div className="max-w-5xl mx-auto">
                    <button
                        onClick={() => navigate('/apprentice/pflanze-der-woche')}
                        className="mb-6 flex items-center gap-2 text-gray-600 hover:text-plantlix-500 dark:text-gray-400 dark:hover:text-plantlix-400 font-bold transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Zurück zur Übersicht
                    </button>

                    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                        {/* Header Section */}
                        <div className="p-8 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-br from-white to-gray-50 dark:from-[#1a1a1a] dark:to-[#1e1e1e]">
                            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2 flex items-center gap-3">
                                <span className="p-2 bg-plantlix-500/10 text-plantlix-500 rounded-xl">
                                    <Plus className="w-7 h-7" />
                                </span>
                                Pflanze der Woche erstellen
                            </h1>
                            <p className="text-gray-500 dark:text-gray-400 max-w-2xl">
                                Wähle eine oder mehrere Pflanzen aus. Wir befüllen das botanische Formular automatisch für dich.
                            </p>
                        </div>

                        {/* Filter & Search Controls */}
                        <div className="p-6 bg-gray-50/50 dark:bg-white/5 border-b border-gray-100 dark:border-gray-800 space-y-6">
                            {/* Search and Tabs */}
                            <div className="flex flex-col lg:flex-row gap-4 items-center">
                                <div className="relative flex-1 w-full">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Name suchen..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-plantlix-500/10 focus:border-plantlix-500 transition-all shadow-sm"
                                    />
                                </div>
                                <div className="flex p-1 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm w-full lg:w-auto">
                                    {[
                                        { id: 'all', label: 'Alle', icon: Layers },
                                        { id: 'favorites', label: 'Favoriten', icon: Sparkles },
                                        { id: 'recent', label: 'Gelernt', icon: CheckCircle2 }
                                    ].map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all flex-1 lg:flex-none ${activeTab === tab.id ? 'bg-plantlix-500 text-white shadow-lg shadow-plantlix-500/20' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                        >
                                            <tab.icon className="w-4 h-4" />
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Categories */}
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setActiveCategory(null)}
                                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${!activeCategory ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-black' : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700 hover:border-plantlix-500'}`}
                                >
                                    Alle Typen
                                </button>
                                {(categories || []).map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setActiveCategory(cat.id)}
                                        className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${activeCategory === cat.id ? 'bg-plantlix-500 text-white border-plantlix-500 shadow-md' : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700 hover:border-plantlix-500'}`}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Selection Bar (Sticky) */}
                        {selectedSearchIds.length > 0 && (
                            <div className="mx-6 mt-6 p-4 bg-plantlix-500 text-white rounded-2xl flex items-center justify-between shadow-xl shadow-plantlix-500/20 animate-in slide-in-from-top-4 duration-300">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center font-black">
                                        {selectedSearchIds.length}
                                    </div>
                                    <div>
                                        <p className="font-black leading-none">Pflanzen ausgewählt</p>
                                        <p className="text-[10px] opacity-80 uppercase tracking-widest font-bold mt-1">Sammel-Erstellung bereit</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleCreateBatchDrafts}
                                    disabled={isCreatingBatch}
                                    className="px-6 py-3 bg-white text-plantlix-600 rounded-xl font-black shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isCreatingBatch ? 'Wird erstellt...' : 'Jetzt erstellen'}
                                    <Plus className="w-5 h-5" />
                                </button>
                            </div>
                        )}

                        {/* Visual Grid Selection */}
                        <div className="p-6">
                            {displayPlants.length === 0 ? (
                                <div className="py-20 text-center flex flex-col items-center">
                                    <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-3xl flex items-center justify-center text-3xl mb-4 text-gray-400">🔍</div>
                                    <p className="font-bold text-gray-500 dark:text-gray-400">Keine passenden Pflanzen gefunden.</p>
                                    <button onClick={() => { setSearchQuery(''); setActiveCategory(null); setActiveTab('all'); }} className="mt-4 text-plantlix-500 font-bold hover:underline">Filter zurücksetzen</button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {displayPlants.map(plant => {
                                        const isSelected = selectedSearchIds.includes(plant.id);
                                        const mainImage = plant.images && plant.images.length > 0 ? plant.images[0] : null;
                                        
                                        return (
                                            <div
                                                key={plant.id}
                                                onClick={() => {
                                                    setSelectedSearchIds(prev => 
                                                        prev.includes(plant.id) 
                                                        ? prev.filter(i => i !== plant.id) 
                                                        : [...prev, plant.id]
                                                    );
                                                }}
                                                className={`group relative aspect-[4/5] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 border-2 ${isSelected ? 'border-plantlix-500 ring-4 ring-plantlix-500/10' : 'border-transparent hover:border-plantlix-500/30 shadow-sm'}`}
                                            >
                                                {/* Plant Image */}
                                                <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800">
                                                    {mainImage ? (
                                                        <OptimizedImage
                                                            src={mainImage.image_small || mainImage.image}
                                                            alt={plant.botanical_name}
                                                            className={`w-full h-full object-cover transition-transform duration-700 ${isSelected ? 'scale-110' : 'group-hover:scale-110'}`}
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <Layers className="w-8 h-8 text-gray-300" />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Gradient Overlay */}
                                                <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity ${isSelected ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'}`} />

                                                {/* Selection Checkbox */}
                                                <div className={`absolute top-3 right-3 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-plantlix-500 border-plantlix-500 scale-110' : 'bg-black/20 border-white/50 backdrop-blur-md'}`}>
                                                    {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                                                </div>

                                                {/* Info Text */}
                                                <div className="absolute bottom-0 left-0 right-0 p-4 transform transition-transform group-hover:-translate-y-1">
                                                    <p className="text-[10px] text-plantlix-400 font-black uppercase tracking-widest mb-1 truncate">
                                                        {(categories || []).find(c => c.id === plant.category)?.name || 'Pflanze'}
                                                    </p>
                                                    <h3 className="text-white font-bold leading-tight line-clamp-2 mb-1">{plant.botanical_name}</h3>
                                                    <p className="text-white/60 text-xs truncate">{plant.german_name || 'Kein deutscher Name'}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        
                        {/* Footer Info */}
                        <div className="px-8 py-4 bg-gray-50 dark:bg-white/5 text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                            <Sparkles className="w-3 h-3 text-plantlix-500" />
                            <span>Tipp: Wähle mehrere Pflanzen für einen Batch-Export</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-8 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <button
                        onClick={() => navigate('/apprentice/pflanze-der-woche')}
                        className="flex items-center gap-2 text-gray-600 hover:text-plantlix-500 dark:text-gray-400 dark:hover:text-plantlix-400"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Zurück
                    </button>

                    <div className="flex gap-3">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-2 px-6 py-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white rounded-lg font-semibold transition-all disabled:opacity-70"
                        >
                            <Save className={`w-4 h-4 ${saving ? 'animate-pulse' : ''}`} />
                            <span className="hidden sm:inline">{saving ? 'Speichert...' : 'Speichern'}</span>
                        </button>
                        <button
                            onClick={handleDownloadPdf}
                            className="flex items-center gap-2 px-6 py-2 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg font-semibold transition-all shadow-md"
                            title="Normalen PDF-Bogen exportieren"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Normales PDF</span>
                        </button>
                        
                        <button
                            onClick={handleDownloadHerbariumPdf}
                            className="flex items-center gap-2 px-6 py-2 bg-plantlix-700 hover:bg-plantlix-800 text-white rounded-lg font-semibold transition-all shadow-md"
                            title="Einseitiges Beiblatt für Herbarien"
                        >
                            <FileText className="w-4 h-4" />
                            <span className="hidden sm:inline">Herbarium PDF</span>
                        </button>

                        {!isNew && (
                            <button
                                onClick={handleDelete}
                                className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400 rounded-lg font-semibold transition-all"
                                title="Löschen"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="mb-6 flex flex-col md:flex-row items-center justify-center gap-6">
                    {/* Batch Research Control */}
                    <div className="flex items-center gap-4 bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <button
                            onClick={handleBatchSuggestion}
                            disabled={isBatchSuggesting || (!selectedPlantId && !isNew)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                                (isBatchSuggesting || (!selectedPlantId && !isNew))
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-plantlix-50 text-plantlix-600 hover:bg-plantlix-100'
                            }`}
                            title={!selectedPlantId ? "Bitte zuerst eine Pflanze verknüpfen" : "Alle leeren Felder mit KI füllen"}
                        >
                            <Sparkles className="w-4 h-4" />
                            KI: Füllen
                        </button>
                        
                        <button
                            onClick={handleMagicFillAndSave}
                            disabled={isBatchSuggesting || (!selectedPlantId && !isNew)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all ${
                                (isBatchSuggesting || (!selectedPlantId && !isNew))
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-plantlix-100 text-plantlix-700 hover:bg-plantlix-200 border border-plantlix-200'
                            }`}
                            title={!selectedPlantId ? "Bitte zuerst eine Pflanze verknüpfen" : "Alles überschreiben, validieren & sofort speichern"}
                        >
                            <CheckCircle2 className="w-4 h-4" />
                            Magic Fill & Verify
                        </button>

                        <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>

                        <label className="flex items-center gap-2 cursor-pointer pr-2">
                            <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded border-gray-300 text-plantlix-500 focus:ring-plantlix-500" 
                                checked={overwriteOnBatch}
                                onChange={(e) => setOverwriteOnBatch(e.target.checked)}
                            />
                            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                                Überschreiben
                            </span>
                        </label>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-1 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex">

                        <button
                            onClick={() => setViewMode('form')}
                            className={`px-6 py-2 rounded-lg font-medium transition-all ${viewMode === 'form' ? 'bg-plantlix-500 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            Formular Ansicht
                        </button>
                        <button
                            onClick={() => setViewMode('pdf')}
                            className={`px-6 py-2 rounded-lg font-medium transition-all ${viewMode === 'pdf' ? 'bg-plantlix-500 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            PDF Ansicht
                        </button>
                    </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex flex-wrap gap-4 p-4 mb-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Berichtswoche (KW)</label>
                            <select
                                value={selectedTrainingWeekId}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedTrainingWeekId(val);
                                    if (val) {
                                        const week = trainingWeeks.find(w => w.id.toString() === val);
                                        if (week) {
                                            const dStart = new Date(week.start_date).toLocaleDateString('de-DE');
                                            const dEnd = new Date(week.end_date).toLocaleDateString('de-DE');
                                            setFormData(prev => ({
                                                ...prev,
                                                kw_num: week.week_number.toString(),
                                                date_start: dStart,
                                                date_end: dEnd,
                                                display_period: `${dStart} bis ${dEnd}`,
                                                kw_from: dStart,
                                                kw_to: dEnd
                                            }));
                                        }
                                    }
                                }}
                                className="w-full bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white p-0 font-medium appearance-none cursor-pointer"
                            >
                                <option value="">Nicht zugeordnet</option>
                                {trainingWeeks.map(week => (
                                    <option key={week.id} value={week.id} className="dark:bg-gray-800">
                                        KW {week.week_number}/{week.year} ({new Date(week.start_date).toLocaleDateString()})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Anzeige Zeitraum (PDF)</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text" name="date_start" value={formData.date_start || ''}
                                    onChange={handleChange} placeholder="Vom"
                                    className="w-20 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white p-0 font-medium"
                                />
                                <span className="text-gray-400">-</span>
                                <input
                                    type="text" name="date_end" value={formData.date_end || ''}
                                    onChange={handleChange} placeholder="Bis"
                                    className="w-20 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white p-0 font-medium"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-8">
                        {viewMode === 'pdf' ? (
                            <div className="flex justify-center">
                                <div className="w-full max-w-4xl">
                                    {pdfCoordinates ? (
                                        <PdfOverlayEditor 
                                            formData={formData} 
                                            setFormData={setFormData} 
                                            coordinates={pdfCoordinates}
                                            onImageUpload={handleImageUpload}
                                            image1Preview={pdwImagePreview1}
                                            image2Preview={pdwImagePreview2}
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center p-20 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                                            <div className="text-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-plantlix-500 mx-auto mb-4"></div>
                                                <p className="text-gray-500">Lade PDF-Koordinaten...</p>
                                            </div>
                                        </div>
                                    )}
                                    <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                        💡 Tipp: Du kannst direkt in die Felder des PDFs klicken und schreiben.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Section 1 */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-6 bg-plantlix-500 rounded-full"></div>
                                        Basis-Informationen
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <InputField label="Botanischer Name" name="botanical_name" formData={formData} handleChange={handleChange} />
                                        <InputField label="Deutscher Name" name="german_name" formData={formData} handleChange={handleChange} />
                                        <InputField label="Familie" name="family" formData={formData} handleChange={handleChange} />
                                        <InputField label="Einordnung (z.B. Laubgehölz)" name="growth_form" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        <InputField label="Heimat" name="origin" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        <InputField label="Natürlicher Standort" name="distribution" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        <div className="col-span-1 md:col-span-2">
                                            <InputField label="Artname-Erklärung" name="artname_erklärung" type="textarea" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        </div>
                                        <div className="col-span-1 md:col-span-2 flex gap-6 mt-2">
                                            <Checkbox label="Züchtung" name="is_zuechtung" formData={formData} handleChange={handleChange} />
                                            <Checkbox label="Heimische Wildpflanze" name="is_heimisch" formData={formData} handleChange={handleChange} />
                                            <Checkbox label="Geschützte Art" name="is_protected" formData={formData} handleChange={handleChange} />
                                            <Checkbox label="Zwischenprüfungs-Pflanze" name="is_intermediate_exam_subject" formData={formData} handleChange={handleChange} />
                                        </div>
                                        <InputField label="Gefährdungsgrad (RL)" name="endangerment" formData={formData} handleChange={handleChange} />
                                    </div>
                                </div>

                                {/* Section 2 */}
                                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-6 bg-plantlix-500 rounded-full"></div>
                                        Detail-Merkmale
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <InputField label="Beobachteter Standort" name="observed_location" formData={formData} handleChange={handleChange} />
                                        <InputField label="Höhe" name="height" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        <InputField label="Breite" name="width" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        <div className="col-span-1 md:col-span-3">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Lebensbereich (Hansen-Stahl-System)
                                            </label>
                                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                                {[
                                                    { code: 'G',     label: 'G – Gehölz' },
                                                    { code: 'GR',    label: 'GR – Gehölzrand' },
                                                    { code: 'Fr',    label: 'Fr – Freifläche' },
                                                    { code: 'B',     label: 'B – Beet' },
                                                    { code: 'SH',    label: 'SH – Steppe/Heide' },
                                                    { code: 'H',     label: 'H – Heide' },
                                                    { code: 'St',    label: 'St – Steinanlage' },
                                                    { code: 'FS',    label: 'FS – Fels-Steppe' },
                                                    { code: 'M',     label: 'M – Matten' },
                                                    { code: 'SF',    label: 'SF – Stein-Fugen' },
                                                    { code: 'MK',    label: 'MK – Mauer-Kronen' },
                                                    { code: 'A',     label: 'A – Alpinum' },
                                                    { code: 'WR',    label: 'WR – Wasser-Rand' },
                                                    { code: 'W',     label: 'W – Wasserpfl.' },
                                                    { code: 'KÜBEL', label: 'KÜBEL – Kübel' },
                                                ].map(({ code, label }) => (
                                                    <label key={code} className="flex items-center gap-1.5 cursor-pointer group">
                                                        <input
                                                            type="checkbox"
                                                            checked={(formData.life_zones || []).includes(code)}
                                                            onChange={() => {
                                                                handleArrayChange('life_zones', code);
                                                                // Keep life_zone text in sync for PDF
                                                                setFormData(prev => {
                                                                    const current = prev.life_zones || [];
                                                                    const next = current.includes(code)
                                                                        ? current.filter(c => c !== code)
                                                                        : [...current, code];
                                                                    return { ...prev, life_zone: next.join(', ') };
                                                                });
                                                            }}
                                                            className="w-3.5 h-3.5 rounded border-gray-300 text-plantlix-500 focus:ring-plantlix-500"
                                                        />
                                                        <span className="text-xs text-gray-700 dark:text-gray-300 group-hover:text-plantlix-500 transition-colors">{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <InputField label="Herbstfärbung" name="autumn_color" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        <InputField label="Blütenfarbe" name="flower_color" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        <div className="col-span-1 md:col-span-3">
                                            <InputField label="Blütenstand" name="flower_inflorescence" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        </div>
                                        <div className="col-span-1 md:col-span-3">
                                            <InputField label="Blätter (Stellung, Form, Rand)" name="leaf_info" type="textarea" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        </div>
                                        <div className="col-span-1 md:col-span-3">
                                            <InputField label="Früchte (Art, Stand)" name="fruit_info" type="textarea" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        </div>
                                    </div>
                                </div>

                                {/* Map Section for PDW */}
                                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                            <div className="w-1.5 h-6 bg-plantlix-500 rounded-full"></div>
                                            Fundort auf Karte
                                        </h3>
                                        <button 
                                            onClick={() => setShowMap(!showMap)}
                                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black transition-all ${showMap ? 'bg-plantlix-500 text-white' : 'bg-gray-600 text-white'}`}
                                        >
                                            {showMap ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                            {showMap ? 'IM PDF' : 'NICHT IM PDF'}
                                        </button>
                                    </div>
                                    
                                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
                                        <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 mb-4">
                                            <LocationMap 
                                                lat={latitude}
                                                lng={longitude}
                                                isEditable={true}
                                                onLocationSelect={(lat, lng) => {
                                                    setLatitude(lat);
                                                    setLongitude(lng);
                                                }}
                                                height="250px"
                                                zoom={13}
                                            />
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <div className="flex-1">
                                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1 tracking-widest">Geographische Breite (Lat)</label>
                                                <input 
                                                    type="number" step="any"
                                                    value={latitude || ''}
                                                    onChange={(e) => setLatitude(e.target.value === '' ? null : parseFloat(e.target.value))}
                                                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white"
                                                    placeholder="z.B. 51.0504"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1 tracking-widest">Geographische Länge (Lng)</label>
                                                <input 
                                                    type="number" step="any"
                                                    value={longitude || ''}
                                                    onChange={(e) => setLongitude(e.target.value === '' ? null : parseFloat(e.target.value))}
                                                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white"
                                                    placeholder="z.B. 13.7373"
                                                />
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-4 text-center italic">
                                            Klicke auf die Karte, um den Standort zu markieren oder gib die Koordinaten manuell ein.
                                        </p>
                                    </div>
                                </div>

                                {/* Section 3: Checkboxes */}
                                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-6 bg-plantlix-500 rounded-full"></div>
                                        Eigenschaften & Ansprüche
                                    </h3>

                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Blütezeit Monate (Römisch I-XII)</label>
                                            <div className="flex flex-wrap gap-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                                                {['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'].map(num => (
                                                    <ArrayCheckbox key={num} label={num} arrayName="flowering_months" value={num} formData={formData} handleArrayChange={handleArrayChange} />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Standortansprüche</label>
                                                <div className="flex flex-col gap-2">
                                                    {['vollsonnig', 'sonnig', 'absonnig', 'halbschattig', 'schattig'].map(opt => (
                                                        <ArrayCheckbox key={opt} label={opt} arrayName="location_preferences" value={opt} formData={formData} handleArrayChange={handleArrayChange} />
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Wasser</label>
                                                <div className="flex flex-col gap-2">
                                                    {['trocken', 'mäßig trocken', 'frisch', 'feucht', 'Wasserstandorte'].map(opt => (
                                                        <ArrayCheckbox key={opt} label={opt} arrayName="watering_needs" value={opt} formData={formData} handleArrayChange={handleArrayChange} />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <InputField label="Bodenansprüche" name="soil_type" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                            <InputField label="Nährstoffbedarf" name="nutrient_needs" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />

                                            <div className="col-span-1 md:col-span-2 flex flex-wrap gap-x-6 gap-y-2 items-center bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mt-2">
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Kalk:</span>
                                                {['liebend', 'tolerierend', 'empfindlich'].map(opt => (
                                                    <ArrayCheckbox key={opt} label={opt} arrayName="lime_tolerance" value={opt} formData={formData} handleArrayChange={handleArrayChange} />
                                                ))}
                                            </div>

                                            <InputField label="pH-Wert" name="soil_ph" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                            <div className="flex items-center gap-6 mt-6">
                                                {['ph_neutral', 'ph_sauer', 'ph_alkalisch'].map(key => (
                                                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData[key] === '☑'}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.checked ? '☑' : 'n' }))}
                                                            className="w-4 h-4 rounded border-gray-300 text-plantlix-500 focus:ring-plantlix-500"
                                                        />
                                                        <span className="text-sm text-gray-700 dark:text-gray-300">{key.replace('ph_', '')}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Section 4 */}
                                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                                                <div className="w-1.5 h-6 bg-plantlix-500 rounded-full"></div>
                                                Verwendung
                                            </h3>
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { k: 'ORNAMENTAL', l: 'Ziergehölz' }, { k: 'EDIBLE', l: 'Nutzpflanze' },
                                                    { k: 'MEDICINAL', l: 'Heilpflanze' }, { k: 'WOOD', l: 'Holzlieferant' },
                                                    { k: 'WIND_BREAK', l: 'Windschutz' }, { k: 'PRIVACY', l: 'Sichtschutz' },
                                                    { k: 'GROUND_COVER', l: 'Bodendecker' }, { k: 'CLIMBING', l: 'Kletterpflanze' },
                                                    { k: 'CONTAINER', l: 'Kübelpflanze' }
                                                ].map(opt => (
                                                    <ArrayCheckbox key={opt.k} label={opt.l} arrayName="uses" value={opt.k} formData={formData} handleArrayChange={handleArrayChange} />
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                                                <div className="w-1.5 h-6 bg-plantlix-500 rounded-full"></div>
                                                Sonstiges
                                            </h3>
                                            <InputField label="Gärtnerische Vermehrung, Pflege, Besonderheiten" name="care_notes" type="textarea" formData={formData} handleChange={handleChange} showSuggestion handleSuggestion={handleSuggestion} suggestingField={suggestingField} />
                                        </div>
                                    </div>
                                </div>

                                {/* Section 5: Images */}
                                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-6 bg-plantlix-500 rounded-full"></div>
                                        Aussehen / Bilder
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Bild 1 */}
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bild 1</label>
                                                <button
                                                    onClick={() => openGallery('image_1')}
                                                    className="text-xs font-semibold text-plantlix-500 hover:text-plantlix-700 flex items-center gap-1"
                                                >
                                                    <Layers className="w-3 h-3" />
                                                    Aus Galerie wählen
                                                </button>
                                            </div>
                                            <div className="relative aspect-video bg-gray-100 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden flex items-center justify-center group">

                                                {(pdwImagePreview1 && pdwImagePreview1 !== 'null' && pdwImagePreview1 !== '') ? (
                                                    <>
                                                        <img
                                                            src={pdwImagePreview1}
                                                            alt="Preview 1"
                                                            className="w-full h-full object-contain"
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <label className="cursor-pointer bg-white text-gray-900 px-4 py-2 rounded-lg font-medium shadow-lg hover:bg-gray-100">
                                                                Ändern
                                                                <input
                                                                    type="file" accept="image/*" className="hidden"
                                                                    onChange={(e) => handleImageUpload(e, 'image_1')}
                                                                />
                                                            </label>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <label className="cursor-pointer flex flex-col items-center gap-2 text-gray-400 hover:text-plantlix-500 transition-colors">
                                                        <ImageIcon className="w-12 h-12" />
                                                        <span className="text-sm font-medium">Bild auswählen</span>
                                                        <input
                                                            type="file" accept="image/*" className="hidden"
                                                            onChange={(e) => handleImageUpload(e, 'image_1')}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        </div>

                                        {/* Bild 2 */}
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bild 2</label>
                                                <button
                                                    onClick={() => openGallery('image_2')}
                                                    className="text-xs font-semibold text-plantlix-500 hover:text-plantlix-700 flex items-center gap-1"
                                                >
                                                    <Layers className="w-3 h-3" />
                                                    Aus Galerie wählen
                                                </button>
                                            </div>
                                            <div className="relative aspect-video bg-gray-100 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden flex items-center justify-center group">

                                                {(pdwImagePreview2 && pdwImagePreview2 !== 'null' && pdwImagePreview2 !== '') ? (
                                                    <>
                                                        <img
                                                            src={pdwImagePreview2}
                                                            alt="Preview 2"
                                                            className="w-full h-full object-contain"
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <label className="cursor-pointer bg-white text-gray-900 px-4 py-2 rounded-lg font-medium shadow-lg hover:bg-gray-100">
                                                                Ändern
                                                                <input
                                                                    type="file" accept="image/*" className="hidden"
                                                                    onChange={(e) => handleImageUpload(e, 'image_2')}
                                                                />
                                                            </label>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <label className="cursor-pointer flex flex-col items-center gap-2 text-gray-400 hover:text-plantlix-500 transition-colors">
                                                        <ImageIcon className="w-12 h-12" />
                                                        <span className="text-sm font-medium">Bild auswählen</span>
                                                        <input
                                                            type="file" accept="image/*" className="hidden"
                                                            onChange={(e) => handleImageUpload(e, 'image_2')}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Image Gallery Modal */}
            {showGallery && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#1a1a1a] w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/20">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Bilder-Galerie</h2>
                                <p className="text-sm text-gray-500">Wähle ein Bild aus der Datenbank oder von Wikipedia aus.</p>
                            </div>
                            <button 
                                onClick={() => setShowGallery(false)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingGallery ? (
                                <div className="flex flex-col items-center justify-center py-20 gap-4">
                                    <div className="w-12 h-12 border-4 border-plantlix-500/20 border-t-plantlix-500 rounded-full animate-spin"></div>
                                    <p className="text-gray-500 animate-pulse">Suche verfügbare Bilder...</p>
                                </div>
                            ) : galleryImages.length === 0 ? (
                                <div className="text-center py-20 text-gray-500">
                                    Keine Bilder gefunden. Du kannst ein eigenes Bild hochladen.
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {galleryImages.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleGallerySelection(img.url)}
                                            className="group relative aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-plantlix-500 transition-all shadow-sm hover:shadow-lg bg-gray-50 dark:bg-gray-800"
                                        >
                                            <img 
                                                src={img.url} 
                                                alt={`Selection ${idx}`} 
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <CheckCircle2 className="w-8 h-8 text-white drop-shadow-md" />
                                            </div>
                                            <div className="absolute bottom-1 right-1 bg-white/90 dark:bg-black/80 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                                {img.source}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="p-6 border-t border-gray-100 dark:border-gray-800 text-right">
                            <button 
                                onClick={() => setShowGallery(false)}
                                className="px-6 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

